import { CashBackPolicy } from '@fruitful/types';
import * as React from 'react';
import {
    ScrollView,
    TouchableWithoutFeedback,
    useWindowDimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Icon,
    isWeb,
    ScanEyeIcon,
    Shadows,
    View,
    YStack,
} from 'smoothie';

import { useUserAuthorization } from '../../../auth/hooks/useUserAuthorization';
import { formatInterestRateOrYieldPercent } from '../../../common/utils/bankingUtils';
import type { User } from '../../../user/types/User';
import { KYCVerificationStatus } from '../../types/BankAccount';
import type InterestRate from '../../types/InterestRate';
import { ProductFeatureDisclosures } from '../Disclosures';
import BackgroundGradient from './BackgroundGradient';
import CashBackSection from './CashBackSection';
import HeroSection from './HeroSection';
import {
    DEFAULT_SPLASH_INNER_COLUMN_MAX_WIDTH,
    getIsIosIPad,
    IPAD_EXTRA_MARGIN_ABOVE_CASHBACK_SECTION,
    NATIVE_PHONE_SPLASH_COLUMN_MAX_WIDTH,
    SM_MEDIA_MIN_WIDTH,
} from './layoutBreakpoints';

interface CashAccountSplashProps {
    currentInterestRate: InterestRate | undefined;
    user: User;
}

const CashAccountSplashInner = ({
    currentInterestRate,
    user,
}: CashAccountSplashProps) => {
    const { width: windowWidth } = useWindowDimensions();

    const productCashBackRate =
        user.membership?.product?.banking?.rateCashBack ?? '2%';
    const productCashApyRate = user.membership?.product?.banking?.rateCashAPY;
    const cashBackPolicyUnrestricted =
        user.membership?.product?.banking.cashBackPolicy ===
        CashBackPolicy.UNRESTRICTED;

    const formattedApy = formatInterestRateOrYieldPercent(
        currentInterestRate?.apy ?? 0,
    );

    const isIosIPad = getIsIosIPad();
    const onWeb = Boolean(isWeb);

    const useLargePhoneMock = windowWidth >= SM_MEDIA_MIN_WIDTH || isIosIPad;

    const wideLayout: boolean =
        (onWeb && windowWidth >= SM_MEDIA_MIN_WIDTH) || (!onWeb && isIosIPad);

    const nativePhoneLayout = !onWeb && !isIosIPad;

    const innerColumnMaxWidth = nativePhoneLayout
        ? NATIVE_PHONE_SPLASH_COLUMN_MAX_WIDTH
        : DEFAULT_SPLASH_INNER_COLUMN_MAX_WIDTH;

    const centeredShellProps = wideLayout
        ? {
              alignSelf: 'center' as const,
              maxWidth: 600,
              paddingHorizontal: 12,
              width: '100%' as const,
          }
        : {};

    const innerSectionProps = wideLayout
        ? {
              gap: '10vh' as const,
              maxWidth: '100%' as const,
              width: '100%' as const,
          }
        : {};

    return (
        <SafeAreaView style={{ flex: 1 }} edges={['left', 'right']}>
            <TouchableWithoutFeedback>
                <ScrollView
                    style={{ flex: 1, marginTop: 16 }} // avoid this covering the header
                    contentContainerStyle={{ flexGrow: 1 }}
                >
                    <BackgroundGradient />
                    <YStack
                        paddingHorizontal={8}
                        $mobile_norm={{ paddingHorizontal: 20 }}
                        gap={24}
                        flex={1}
                        justifyContent="center"
                        minHeight="100%"
                        {...centeredShellProps}
                    >
                        <YStack
                            gap={24}
                            width="100%"
                            maxWidth={innerColumnMaxWidth}
                            alignSelf="center"
                            {...innerSectionProps}
                        >
                            <HeroSection
                                formattedApy={
                                    productCashApyRate ?? formattedApy
                                }
                                useLargePhoneMock={useLargePhoneMock}
                            />
                            <YStack
                                marginTop={
                                    isIosIPad
                                        ? IPAD_EXTRA_MARGIN_ABOVE_CASHBACK_SECTION
                                        : 0
                                }
                            >
                                <CashBackSection
                                    cashBackRate={productCashBackRate}
                                    cashBackPolicyUnrestricted={
                                        cashBackPolicyUnrestricted
                                    }
                                />
                            </YStack>
                        </YStack>
                        <ProductFeatureDisclosures
                            activeDisclosures={['CASH', 'CARD']}
                        />
                    </YStack>
                </ScrollView>
            </TouchableWithoutFeedback>
        </SafeAreaView>
    );
};

const CashAccountSplash = ({
    currentInterestRate,
    user,
}: CashAccountSplashProps) => {
    const { accessToRestrictedCapabilities } = useUserAuthorization();
    const { syncteraKycStatus } = user;

    const canOpenAccounts = React.useMemo(() => {
        const isAccountOpeningDisabled =
            !accessToRestrictedCapabilities.openBankAccount ||
            (syncteraKycStatus != null &&
                [
                    KYCVerificationStatus.PENDING,
                    KYCVerificationStatus.REVIEW,
                    KYCVerificationStatus.REJECTED,
                ].includes(syncteraKycStatus));

        return !isAccountOpeningDisabled;
    }, [accessToRestrictedCapabilities.openBankAccount, syncteraKycStatus]);

    if (!canOpenAccounts) {
        return <CannotOpenAccountView />;
    }

    return (
        <CashAccountSplashInner
            currentInterestRate={currentInterestRate}
            user={user}
        />
    );
};

const CannotOpenAccountView = () => {
    return (
        <SafeAreaView style={{ flex: 1, backgroundColor: '$white' }}>
            <YStack flex={1} padding={16}>
                <YStack
                    flex={1}
                    justifyContent="center"
                    alignItems="center"
                    $sm={{
                        justifyContent: 'flex-start',
                        marginTop: '10%',
                    }}
                >
                    <View
                        style={{
                            padding: 12,
                            marginBottom: 16,
                            justifyContent: 'center',
                            backgroundColor: '$white',
                            alignItems: 'center',
                            borderRadius: 16,
                            ...Shadows.LARGE,
                        }}
                    >
                        <Icon size={50} icon={ScanEyeIcon} color="$gray1200" />
                    </View>
                    <BodyLargeEmphasized marginBottom={8} textAlign="center">
                        We&apos;re reviewing your information
                    </BodyLargeEmphasized>
                    <BodyMediumRegular color="$gray1000" textAlign="center">
                        A Fruitful team member may reach out for additional
                        information.
                    </BodyMediumRegular>
                </YStack>
            </YStack>
        </SafeAreaView>
    );
};

export default CashAccountSplash;
